import { motion } from 'framer-motion';
import { css } from 'emotion';
import { Text } from '../../../shared-components/text';
import { Box } from '../../../shared-components/Box';
import { Colors } from '@be-brite/briteui';
import { ChartScatterIcon, CheckIcon, HeartIcon, PulseIcon, StethoscopeIcon } from '@phosphor-icons/react';
import { useCallback } from 'react';
import { shadows } from '../../../shared-components/shared-styles';
import { mediaQueries } from '../../../shared-components';

const colorMap = {
  LowUsage: [Colors.Green100, Colors.Green300],
  MediumUsage: ['rgb(37, 99, 235)', 'rgb(219, 234, 254)'],
  HighUsage: [Colors.Yellow100, 'rgb(252, 245, 230)'],
  Custom: [Colors.Black, Colors.Gray100],
};

const iconMap = {
  LowUsage: StethoscopeIcon,
  MediumUsage: HeartIcon,
  HighUsage: PulseIcon,
  Custom: ChartScatterIcon,
};

export const DecisionToolUsageProfileQuestion = ({ preset, setPreset, presets }) => {
  const Icon = useCallback(({ type }) => {
    const MappedIcon = iconMap[type];
    const [color, backgroundColor] = colorMap[type];

    return (
      <Box
        css={`
          border-radius: 8px;
          background-color: ${backgroundColor};
          padding: 4px;
        `}
        flex="center"
      >
        <MappedIcon size={24} color={color} />
      </Box>
    );
  }, []);

  return (
    <motion.div
      key="DecisionToolUsageProfileQuestion"
      initial="pageInitial"
      animate="pageAnimate"
      exit="pageExit"
      variants={{
        pageInitial: {
          y: 400,
          opacity: 0,
        },
        pageAnimate: {
          y: 0,
          opacity: 1,
        },
        pageExit: {
          y: -400,
          opacity: 0,
        },
      }}
      className={css`
        background: #ffffff;
        box-shadow: 0px 2px 8px rgba(37, 40, 45, 0.25);
        border-radius: 8px;
        padding: 24px;
        margin-bottom: 32px;
        text-align: left;
      `}
    >
      <Text
        h2
        css={`
          margin-bottom: 8px;
        `}
      >
        How often do you expect to use medical services?
      </Text>
      <Text>Your expected healthcare usage helps us recommend the most appropriate plan for your needs.</Text>
      <Box
        flex="left stretch"
        css={`
          gap: 16px;
          margin-top: 16px;
          ${mediaQueries.sm} {
            flex-direction: column;
          }
        `}
      >
        {presets.slice(1)?.map((item) => {
          const [description, subscript] = item?.Description?.split(/\r?\n/).filter((d) => d);

          return (
            <Box
              css={`
                display: grid;
                flex-grow: 1;
                gap: 16px;
                border-radius: 8px;
                border: 2px solid ${Colors.Gray300};
                background-color: white;
                padding: 16px;
                cursor: pointer;
                transition: filter 0.2s ease, transform 0.2s ease;
                ${preset === item?.Key
                  ? `
                  ${shadows.md}
                  outline: 2px solid var(--button-background-color);
                  outline-offset: -2px;
                  transform: scale(1.02);
                  `
                  : ''}
                :hover {
                  ${shadows.md}
                }
              `}
              onClick={() => setPreset(item?.Key)}
            >
              <Box
                flex="space-between"
                css={`
                  width: 100%;
                  gap: 8px;
                `}
              >
                <Icon type={item?.Key} />
                <Box
                  css={`
                    border-radius: 50%;
                    border: 2px solid var(--accent-color-4);
                    width: 20px;
                    height: 20px;
                    ${preset === item?.Key
                      ? `
                        border: 2px solid var(--button-background-color);
                        background-color: var(--button-background-color);
                      `
                      : ''}
                  `}
                  flex="center"
                >
                  {preset === item?.Key ? <CheckIcon size={16} weight="bold" color={Colors.White} /> : null}
                </Box>
              </Box>

              <Text h4>{item?.DisplayName}</Text>
              <Text label subtitle>
                {description}
              </Text>
              <Text caption>{subscript}</Text>
            </Box>
          );
        })}
      </Box>
      <Box
        css={`
          display: grid;
          gap: 8px;
          width: calc(100% - 32px);
          margin: 16px 0;
          background-color: ${Colors.White};
          border: 2px solid ${Colors.Gray300};
          border-radius: 8px;
          padding: 16px;
          cursor: pointer;
          transition: filter 0.2s ease, transform 0.2s ease;
          ${preset === ''
            ? `
                ${shadows.md}
                outline: 2px solid var(--button-background-color);
                outline-offset: -2px;
                transform: scale(1.02);
                `
            : ''}
          :hover {
            ${shadows.md}
          }
        `}
        onClick={() => setPreset('')}
      >
        <Box flex="space-between">
          <Box
            flex="left"
            css={`
              gap: 8px;
            `}
          >
            <Icon type="Custom" />
            <Text h4>Run my own scenario</Text>
          </Box>
          <Box
            css={`
              border-radius: 50%;
              border: 2px solid var(--accent-color-4);
              width: 20px;
              height: 20px;
              ${preset === ''
                ? `
                  border: 2px solid var(--button-background-color);
                  background-color: var(--button-background-color);
                `
                : ''}
            `}
            flex="center"
          >
            {preset === '' ? <CheckIcon size={16} weight="bold" color={Colors.White} /> : null}
          </Box>
        </Box>
        <Box>
          <Text label subtitle>
            Create your profile to get personalized recommendations
          </Text>
          <Text caption>Get the most accurate recommendations based on your specific situation and preferences.</Text>
        </Box>
      </Box>
    </motion.div>
  );
};
